import React, { useState } from 'react';
import { DeviceMobile, DownloadSimple, Info } from '@phosphor-icons/react';
import { usePWAInstall } from '../hooks/usePWAInstall';

/**
 * Compact PWA install button used on the Login page.
 * - Only renders on mobile / tablet devices (and not when already installed)
 * - Uses beforeinstallprompt when available
 * - Falls back to iOS instructions modal when unsupported
 */
const PWAInstallButton = () => {
  const { canInstall, installed, platform, install } = usePWAInstall();
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  if (installed) return null;
  if (!platform.isMobile && !platform.isTablet) return null;

  const handleClick = async () => {
    if (canInstall) {
      await install();
    } else if (platform.isIOS) {
      setShowIOSHelp(true);
    } else {
      setShowIOSHelp(true); // generic help for browsers without prompt event
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="pwa-install-btn"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 transition-colors"
      >
        <DeviceMobile size={20} weight="regular" />
        <DownloadSimple size={18} weight="bold" />
        Installer l&apos;application LBC sur cet appareil
      </button>

      {showIOSHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowIOSHelp(false)}
          data-testid="pwa-install-help-modal"
        >
          <div
            className="bg-white rounded-lg max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <Info size={24} className="text-blue-600 flex-shrink-0" weight="fill" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Installer LBC sur votre appareil</h3>
                <p className="text-sm text-slate-600">
                  Suivez ces étapes pour ajouter l&apos;app à votre écran d&apos;accueil.
                </p>
              </div>
            </div>
            {platform.isIOS ? (
              <ol className="space-y-2 text-sm text-slate-800 mb-4 list-decimal list-inside">
                <li>Ouvrez la page dans <strong>Safari</strong> (pas Chrome).</li>
                <li>Appuyez sur l&apos;icône <strong>Partager</strong> ⎋ en bas de l&apos;écran.</li>
                <li>Faites défiler et choisissez <strong>« Sur l&apos;écran d&apos;accueil »</strong>.</li>
                <li>Confirmez avec <strong>Ajouter</strong>.</li>
              </ol>
            ) : (
              <ol className="space-y-2 text-sm text-slate-800 mb-4 list-decimal list-inside">
                <li>Ouvrez le menu <strong>⋮</strong> (trois points) de votre navigateur.</li>
                <li>Choisissez <strong>« Ajouter à l&apos;écran d&apos;accueil »</strong> ou <strong>« Installer l&apos;application »</strong>.</li>
                <li>Confirmez pour installer LBC comme une app native.</li>
              </ol>
            )}
            <button
              onClick={() => setShowIOSHelp(false)}
              data-testid="pwa-install-help-close"
              className="w-full mt-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium"
            >
              J&apos;ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;
